import { Injectable } from '@angular/core';
import { StoredFilter } from '../models/filter-models';
import { FilterImportResults } from '../models/filter-import.models';
import { OAuthService } from './o-auth.service';
import { SettingsService } from './settings.service';
import { FilterStoreService } from './filter-store.service';
import { FilterImporterService } from './filter-importer.service';
import { ToastService } from './toast.service';
import { computeItemHash } from '../utils/item-hash';
import { trackError } from '../app.config';
import { apiBaseUrl, domainName } from '../../game';
import { isApiRateLimited, notifyApiRateLimited } from '../utils/api-rate-limit';

export type FilterSnapshotAction = 'sync' | 'copy' | 'save' | 'download';

export interface SharedFilterRef {
  filterId: string;
  name?: string | null;
  username?: string | null;
}

const DEDUP_WINDOW_MS = 10_000;
const FILTER_ID_PATTERN = /^[0-9A-Za-z]+$/;
const MAX_FILTER_ID_LENGTH = 64;

@Injectable({
  providedIn: 'root',
})
export class FilterSnapshotService {
  private lastUploadKey: string | null = null;
  private lastUploadAt = 0;

  constructor(
    private oauthService: OAuthService,
    private settings: SettingsService,
    private filterStoreService: FilterStoreService,
    private filterImporterService: FilterImporterService,
    private toastService: ToastService,
  ) {}

  uploadSnapshot(storedFilter: StoredFilter, action: FilterSnapshotAction): void {
    if (!this.settings.filterSnapshotStorageEnabled()) return;

    const sessionToken = this.oauthService.sessionToken();
    const filterId = storedFilter.filter?.id;
    if (!sessionToken || !filterId) return;

    void this.uploadSnapshotAsync(storedFilter, action, sessionToken, filterId);
  }

  // Fire-and-forget: records a unique share import for the logged-in account (server dedupes).
  recordShareImport(filterId: string): void {
    if (!this.isValidFilterId(filterId)) return;

    const sessionToken = this.oauthService.sessionToken();
    if (!sessionToken) return;

    void this.recordShareImportAsync(filterId, sessionToken);
  }

  confirmStorageConsent(allowUsername: boolean): void {
    this.settings.allowFilterSnapshotStorage();
    void this.setUsernameSharingPreference(allowUsername);
  }

  declineStorageConsent(): void {
    this.settings.optOutFilterSnapshotStorage();
    void this.setUsernameSharingPreference(false);
  }

  async setUsernameSharingPreference(allow: boolean): Promise<void> {
    this.settings.setFilterSnapshotUsernameEnabled(allow);

    const sessionToken = this.oauthService.sessionToken();
    if (!sessionToken) return;

    try {
      const response = await fetch('/api/username-preference', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ allow_username: allow }),
      });

      if (isApiRateLimited(response)) {
        notifyApiRateLimited(this.toastService);
        return;
      }

      if (!response.ok) {
        trackError(new Error(`Username preference update failed: ${response.status}`));
      }
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  buildShareUrl(storedFilter: StoredFilter): string | null {
    const filterId = storedFilter.filter?.id;
    if (!filterId) return null;

    const params = new URLSearchParams();
    params.set('name', storedFilter.name);

    const username = this.oauthService.userName();
    if (this.settings.filterShareLinkUsernameEnabled() && username) {
      params.set('username', username);
    }

    return `${domainName}/f/${filterId}?${params.toString()}`;
  }

  parseShareInput(text: string): SharedFilterRef | null {
    const trimmed = text.trim();
    if (!trimmed) return null;

    const urlMatch = trimmed.match(/(?:https?:\/\/[^/\s]+)?\/f\/([0-9A-Za-z]+)(?:\?([^#\s]*))?/i);
    if (urlMatch) {
      const filterId = urlMatch[1];
      if (!this.isValidFilterId(filterId)) return null;

      const params = new URLSearchParams(urlMatch[2] ?? '');
      return {
        filterId,
        name: params.get('name'),
        username: params.get('username'),
      };
    }

    if (this.isValidFilterId(trimmed)) {
      return { filterId: trimmed };
    }

    return null;
  }

  async loadSharedFilter(ref: SharedFilterRef): Promise<FilterImportResults | null> {
    const filterId = ref.filterId;
    const displayName = ref.name?.trim() || null;
    const username = ref.username?.trim() || null;

    try {
      const response = await fetch(`${apiBaseUrl}/api/filters/${encodeURIComponent(filterId)}`);

      if (isApiRateLimited(response)) {
        notifyApiRateLimited(this.toastService);
        return null;
      }

      if (response.status === 404) {
        this.toastService.raise('warning', this.buildNotFoundMessage(displayName, filterId, username));
        return null;
      }

      if (!response.ok) {
        this.toastService.raise('warning', `Could not load filter with id ${filterId}.`);
        return null;
      }

      const body = await response.json() as {
        filter_config: unknown;
        build?: number;
        filter_version?: string;
        filter_name?: string;
      };

      if (!body?.filter_config || typeof body.filter_config !== 'object') {
        this.toastService.raise('warning', `Could not load filter with id ${filterId}.`);
        return null;
      }

      const suggestedName = displayName || body.filter_name || 'Imported Filter';
      const storedShape = {
        name: suggestedName,
        filter: body.filter_config,
        version: body.filter_version ?? '',
        build: body.build ?? 0,
        lastUpdated: new Date().toISOString(),
      };

      const importResult = this.filterImporterService.importFilterJson(JSON.stringify(storedShape));
      if (!importResult.importFilterSuccess || !importResult.filter) {
        this.toastService.raise(
          'warning',
          importResult.importFilterError || `Could not load filter with id ${filterId}.`
        );
        return null;
      }

      importResult.importedName = suggestedName;
      return importResult;
    } catch (error) {
      // Cross-origin calls from localhost: edge 429s often lack CORS headers, so fetch
      // rejects instead of returning response.status === 429.
      if (apiBaseUrl) {
        notifyApiRateLimited(this.toastService);
        return null;
      }

      trackError(error instanceof Error ? error : new Error(String(error)));
      this.toastService.raise('warning', `Could not load filter with id ${filterId}.`);
      return null;
    }
  }

  private buildNotFoundMessage(name: string | null, filterId: string, username: string | null): string {
    const namePart = name ? ` ${name}` : '';
    const userPart = username ? ` from user ${username}` : '';
    return `Filter${namePart} with id ${filterId}${userPart} does not exist.`;
  }

  private isValidFilterId(filterId: string): boolean {
    return !!filterId && filterId.length <= MAX_FILTER_ID_LENGTH && FILTER_ID_PATTERN.test(filterId);
  }

  private async recordShareImportAsync(filterId: string, sessionToken: string): Promise<void> {
    try {
      const response = await fetch(`/api/filters/${encodeURIComponent(filterId)}/share`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionToken}`,
        },
      });

      if (isApiRateLimited(response)) {
        return;
      }

      if (!response.ok && response.status !== 404) {
        trackError(new Error(`Share import record failed: ${response.status}`));
      }
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private async uploadSnapshotAsync(
    storedFilter: StoredFilter,
    action: FilterSnapshotAction,
    sessionToken: string,
    filterId: string
  ): Promise<void> {
    try {
      const allowUsername = this.settings.filterSnapshotUsernameEnabled();
      const filterConfigJson = JSON.stringify(storedFilter.filter);
      const configHash = await computeItemHash(null, filterConfigJson);
      const dedupKey = `${filterId}:${configHash}:${action}:${allowUsername}`;
      const now = Date.now();

      if (this.lastUploadKey === dedupKey && now - this.lastUploadAt < DEDUP_WINDOW_MS) {
        return;
      }

      this.lastUploadKey = dedupKey;
      this.lastUploadAt = now;

      const response = await fetch('/api/filters', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filter_id: filterId,
          poe_filter_id: storedFilter.id ?? null,
          filter_name: storedFilter.name,
          filter_version: storedFilter.version,
          build: storedFilter.build,
          filter_config: storedFilter.filter,
          action,
          allow_username: allowUsername,
        }),
      });

      if (isApiRateLimited(response)) {
        return;
      }

      if (!response.ok) {
        trackError(new Error(`Filter snapshot upload failed: ${response.status}`));
        return;
      }

      try {
        const body = await response.json() as { filter_id?: string };
        if (typeof body?.filter_id === 'string' && body.filter_id && body.filter_id !== filterId) {
          this.filterStoreService.updateInternalFilterId(storedFilter.name, body.filter_id);
          storedFilter.filter.id = body.filter_id;
        }
      } catch {
        // Response body optional for older servers; ignore parse failures.
      }

      this.filterStoreService.markSnapshotUploaded(storedFilter.name);
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)));
    }
  }
}
